import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const LocationSearch = ({ onSearch, onFilterChange, onViewToggle, currentView, totalLocations }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    status: 'all',
    services: 'all',
    distance: 'all'
  });

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...activeFilters, [filterType]: value };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    setActiveFilters({
      status: 'all',
      services: 'all',
      distance: 'all'
    });
    setSearchQuery('');
    onSearch('');
    onFilterChange({
      status: 'all',
      services: 'all',
      distance: 'all'
    });
  };

  const hasActiveFilters = Object.values(activeFilters)?.some(filter => filter !== 'all') || searchQuery;

  return (
    <div className="bg-card rounded-xl p-3 sm:p-6 shadow-elevation-1 mb-4 sm:mb-6">
      <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
        {/* Search Input */}
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search by area, landmark, or location name..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>

        {/* Filter Buttons - Compact on Mobile */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          {/* Status Filter */}
          <div className="relative">
            <select
              value={activeFilters?.status}
              onChange={(e) => handleFilterChange('status', e?.target?.value)}
              className="appearance-none bg-muted border border-border rounded-lg px-3 sm:px-4 py-2 pr-6 sm:pr-8 text-xs sm:text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full"
            >
              <option value="all">All Status</option>
              <option value="open">Open Now</option>
              <option value="closed">Closed</option>
            </select>
            <Icon name="ChevronDown" size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none sm:w-4 sm:h-4" />
          </div>

          {/* Services Filter */}
          <div className="relative">
            <select
              value={activeFilters?.services}
              onChange={(e) => handleFilterChange('services', e?.target?.value)}
              className="appearance-none bg-muted border border-border rounded-lg px-3 sm:px-4 py-2 pr-6 sm:pr-8 text-xs sm:text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full"
            >
              <option value="all">All Services</option>
              <option value="wash">Wash & Fold</option>
              <option value="dry-clean">Dry Clean</option>
              <option value="iron">Iron & Press</option>
              <option value="pickup">Pickup & Delivery</option>
            </select>
            <Icon name="ChevronDown" size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none sm:w-4 sm:h-4" />
          </div>

          {/* Distance Filter */}
          <div className="relative">
            <select
              value={activeFilters?.distance}
              onChange={(e) => handleFilterChange('distance', e?.target?.value)}
              className="appearance-none bg-muted border border-border rounded-lg px-3 sm:px-4 py-2 pr-6 sm:pr-8 text-xs sm:text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full"
            >
              <option value="all">Any Distance</option>
              <option value="1km">Within 1km</option>
              <option value="3km">Within 3km</option>
              <option value="5km">Within 5km</option>
            </select>
            <Icon name="ChevronDown" size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none sm:w-4 sm:h-4" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={clearFilters}
              className="text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Clear</span>
              <span className="sm:hidden">×</span>
            </Button>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex items-center bg-muted rounded-lg p-1">
          <button
            onClick={() => onViewToggle('list')}
            className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-smooth flex items-center gap-1 sm:gap-2 ${
              currentView === 'list' ?'bg-card text-foreground shadow-elevation-1' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="List" size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">List</span>
          </button>
          <button
            onClick={() => onViewToggle('map')}
            className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-smooth flex items-center gap-1 sm:gap-2 ${
              currentView === 'map' ?'bg-card text-foreground shadow-elevation-1' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Map" size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Map</span>
          </button>
        </div>
      </div>
      {/* Results Summary - Compact on Mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border">
        <div className="flex items-center text-xs sm:text-sm text-muted-foreground">
          <Icon name="MapPin" size={14} className="mr-1 sm:mr-2 sm:w-4 sm:h-4" />
          <span>
            Showing {totalLocations} collection point{totalLocations !== 1 ? 's' : ''} in Coimbatore
          </span>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          iconName="Locate"
          iconPosition="left"
          className="text-primary hover:text-primary text-xs sm:text-sm self-start sm:self-auto"
        >
          <span className="hidden sm:inline">Use My Location</span>
          <span className="sm:hidden">My Location</span>
        </Button>
      </div>
    </div>
  );
};

export default LocationSearch;